import { keyBy } from 'lodash';
import { createSelector } from 'reselect';
import { NextRouter } from 'next/router';

import {
  AppState,
  RestaurantBranch,
  RestaurantOrg,
  BranchBusinessModel,
} from 'lib/types/global';

import {
  RS_HOME_DELIVERY_AREAS,
  RS_HOME_DELIVERY_AREAS_V2,
  RS_HOME_DELIVERY_AREAS_V2_RADIUS,
  RS_HOME_DELIVERY_AREAS_V3_3_MULTI_RADIUS,
  RS_HOME_DISABLE_DELIVERY_AREAS,
} from 'lib/common/features';

import { getSessionFeatures } from 'lib/store/session/selectors';
import * as f from 'lib/common/features';
import {
  getBranchId,
  getOrgId as getOrgIdFromState,
} from '../dataFilters/selectors';
import { OrgMap, RestaurantsState } from './reducer';

const ALL = 'all';

export const getRestaurantsState = (state: AppState): RestaurantsState =>
  state.restaurants;

export const getAllOrgs = (state: AppState): RestaurantOrg[] =>
  state.restaurants.orgs ?? [];
export const getAllBranches = (state: AppState): RestaurantBranch[] =>
  state.restaurants.branches ?? [];
export const isShortDrnEnabled = (state: AppState) =>
  getSessionFeatures(state)?.has(f.RS_SHOW_SHORT_DRN);

export const getRestaurantCities = (state: AppState): string[] => {
  const branches = state.restaurants.branches ?? [];
  return branches.reduce<string[]>((acc, branch) => {
    if (typeof branch.city === 'undefined' || acc.includes(branch.city)) {
      return acc;
    }

    acc.push(branch.city);
    return acc;
  }, []);
};

export const getBranchesByOrg = (
  { restaurants }: Pick<AppState, 'restaurants'>,
  orgId?: string,
): RestaurantBranch[] =>
  typeof orgId !== 'undefined' ? restaurants.branchesByOrg[orgId] ?? [] : [];

type RestaurantId = Extract<keyof RestaurantBranch, 'id' | 'drnId'>;

export const getBranchesFromIds = (
  branches: RestaurantBranch[],
  branchIds: RestaurantBranch[RestaurantId][],
  idKey: RestaurantId = 'id',
): RestaurantBranch[] => {
  const idToBranch: Partial<
    Record<RestaurantBranch[RestaurantId], RestaurantBranch>
  > = keyBy(branches, idKey);

  return branchIds
    .map((id) => idToBranch[id])
    .filter((branch): branch is RestaurantBranch => !!branch);
};

export const getBranch = (
  state: AppState,
  id?: string,
): RestaurantBranch | undefined => {
  if (typeof id !== 'undefined') {
    return parseInt(id, 10)
      ? state.restaurants.branchesById[id]
      : state.restaurants.branchesByShortDrn[id];
  }
  return undefined;
};

export const branchExists = (state: AppState, id?: string) =>
  !!getBranch(state, id);

export const getOrg = (
  { restaurants }: Pick<AppState, 'restaurants'>,
  id?: string,
): RestaurantOrg | undefined => {
  if (typeof id !== 'undefined') {
    return parseInt(id, 10)
      ? restaurants.orgsById[id]
      : restaurants.orgsByShortDrn[id];
  }
  return undefined;
};

export const orgExists = (state: AppState, id?: string) => !!getOrg(state, id);

export const getOrgId = (
  state: AppState,
  query: Partial<NextRouter['query']>,
): string | undefined => {
  if (typeof query.orgId === 'undefined') return state.restaurants.orgs[0]?.id;
  const orgId = query.orgId as string;
  return (
    (parseInt(orgId, 10)
      ? state.restaurants.orgsById[orgId]
      : state.restaurants.orgsByShortDrn[orgId]
    )?.id ?? state.restaurants.orgs[0]?.id
  );
};

export const branchInOrg = (
  state: AppState,
  branchId?: string,
  orgId?: string,
): boolean =>
  branchExists(state, branchId) &&
  orgExists(state, orgId) &&
  (getBranch(state, branchId)?.orgId === orgId ||
    getBranch(state, branchId)?.orgShortDrn === orgId);

export const getFirstBranchId = (
  state: AppState,
  orgId?: string,
): string | undefined => {
  const branches = getBranchesByOrg(state, orgId);
  return branches.length ? branches[0].id : undefined;
};

export const isBranchDisabled = (state: AppState, branchId?: string) => {
  if (!branchId) {
    // if there's no branch id (e.g. on marketer) then we
    // should consider this as not disabled until we have one
    return false;
  }

  return !getBranch(state, branchId)?.isActive;
};

const HOP_BRANCH_BUSINESS_MODELS = [
  BranchBusinessModel.HOP_DELIVEROO_MANAGED,
  BranchBusinessModel.HOP_PARTNER_MANAGED,
];

export const isHopSite = (state: AppState, branchId: string) => {
  const branch = getBranch(state, branchId);

  return (
    branch &&
    branch.branchBusinessModel &&
    HOP_BRANCH_BUSINESS_MODELS.includes(branch.branchBusinessModel)
  );
};

export const getCurrentOrg = createSelector(
  [
    (state: AppState) => state.restaurants.orgsById,
    (state: AppState) => state.restaurants.orgsByShortDrn,
    getOrgIdFromState,
  ],
  (orgsById: OrgMap, orgsByShortDrn: OrgMap, orgId?: string) => {
    if (typeof orgId !== 'undefined') {
      return parseInt(orgId, 10) ? orgsById[orgId] : orgsByShortDrn[orgId];
    }
    return undefined;
  },
);

// Computed
export const getCurrentBranch = createSelector(
  [getAllBranches, getBranchId],
  (allBranches, branchId) =>
    allBranches.find(
      ({ id, shortDrn }) => id === branchId || shortDrn === branchId,
    ),
);

function getFeatures(
  orgOrBranch?: RestaurantOrg | RestaurantBranch,
): Set<string> {
  return new Set(orgOrBranch?.features);
}

export const getOrgFeatures = (state: AppState, orgId?: string) => {
  const org = getOrg(state, orgId);
  return getFeatures(org);
};

export const getCurrentOrgFeatures = createSelector(getCurrentOrg, getFeatures);

export const getCurrentBranchFeatures = createSelector(
  getCurrentBranch,
  getFeatures,
);

export const getAllOrgIds = createSelector([getAllOrgs], (orgs) =>
  orgs.map((org) => org.id),
);

export const hasBranchWithAreasAndFeesAccess = createSelector(
  [getAllBranches],
  (branches) =>
    Boolean(
      branches.find(
        (branch) =>
          !branch.features.includes(RS_HOME_DISABLE_DELIVERY_AREAS) &&
          (branch.features.includes(RS_HOME_DELIVERY_AREAS) ||
            branch.features.includes(RS_HOME_DELIVERY_AREAS_V2) ||
            branch.features.includes(RS_HOME_DELIVERY_AREAS_V2_RADIUS) ||
            branch.features.includes(RS_HOME_DELIVERY_AREAS_V3_3_MULTI_RADIUS)),
      ),
    ),
);

export const selectBranchesByOrgId = createSelector(
  [getOrgIdFromState, getRestaurantsState],
  (orgId, restaurants) => {
    if (!orgId) {
      return [];
    }
    return orgId === ALL
      ? restaurants.branches || []
      : getBranchesByOrg({ restaurants }, orgId);
  },
);

export const selectOrgHasActiveBranch = createSelector(
  [getOrgIdFromState, selectBranchesByOrgId],
  (orgId, branches) => {
    if (!orgId) {
      return true;
    }
    return branches.some((b) => b.isActive);
  },
);

export const getAllBranchesFeatures = createSelector(
  [selectBranchesByOrgId],
  (branches) => {
    const features = new Set();
    branches?.forEach((branch) => {
      branch.features.forEach(Set.prototype.add.bind(features));
    });
    return features;
  },
);
